import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Divider, Avatar, Chip, useTheme, useMediaQuery, IconButton, Stack, Button
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Home as HomeIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  Receipt as ReceiptIcon,
  SendToMobile as SendToMobileIcon,
  CheckCircle as CheckCircleIcon,
  LocationCity as PropertyIcon,
  ArrowBack,
  ArrowForward
} from '@mui/icons-material';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import { styled } from '@mui/material/styles';
// import ChkInArticleModal from './ChkInArticleModal';

const StatusBadge = styled(Chip)(({ theme, statuscolor }) => ({
  backgroundColor: statuscolor || theme.palette.success.main,
  color: '#fff',
  fontWeight: 600,
  borderRadius: 8,
  textTransform: 'uppercase',
  fontSize: '0.75rem',
}));

const StatCard = styled(Card)(({ theme }) => ({
  height: '100%',
  background: theme.palette.background.paper,
  borderRadius: 16,
  boxShadow: theme.shadows[3],
  transition: 'transform 0.3s, box-shadow 0.3s',
  '&:hover': {
    transform: 'scale(1.02)',
    boxShadow: theme.shadows[6],
  },
}));

const StatIcon = styled(Avatar)(({ theme, bgcolor }) => ({
  background: `linear-gradient(135deg, ${bgcolor || theme.palette.primary.main} 30%, ${theme.palette.grey[700]
    } 90%)`,
  color: '#fff',
  width: 48,
  height: 48,
  marginRight: theme.spacing(2),
}));

const ProfileTab = ({ bookingData, onNext, onPrev, currentIndex, totalBookings }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [openModal, setOpenModal] = useState(false);

  const getFirstLetter = (fullName) => {
    return fullName ? fullName.charAt(0).toUpperCase() : '?';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  if (!bookingData) {
    return (
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Typography variant="h6">No booking data available</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 3 }}>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'center' : 'flex-start',
            gap: 2
          }}>
            {bookingData?.ImageUrl ? (
              <Avatar
                src={bookingData.ImageUrl}
                alt="User"
                sx={{
                  width: 120,
                  height: 120,
                  boxShadow: 3,
                  alignSelf: isMobile ? 'center' : 'flex-start'
                }}
              />
            ) : (
              <Avatar
                sx={{
                  width: 120,
                  height: 120,
                  bgcolor: theme.palette.primary.main,
                  fontSize: '3rem',
                  boxShadow: 3,
                  alignSelf: isMobile ? 'center' : 'flex-start'
                }}
              >
                {getFirstLetter(bookingData?.ResidentName)}
              </Avatar>
            )}

            <Box sx={{ flex: 1, width: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="h4" component="h3" sx={{ fontWeight: 'bold' }}>
                  {bookingData?.ResidentName || 'Resident'}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'nowrap', // Prevent wrapping
                  gap: 1,
                }}
              >
                {/* Left Side: Mobile Number */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flex: 1,
                    minWidth: 0, // allow shrinking
                    overflow: 'hidden', // avoid layout break
                  }}
                >
                  <PhoneAndroidIcon color="action" sx={{ mr: 1, flexShrink: 0 }} />
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}
                  >
                    {bookingData?.MobileNo || 'N/A'}
                  </Typography>
                </Box>

                {/* Right Side: Button + Status */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flexShrink: 0,
                  }}
                >
                  {/* <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => setOpenModal(true)}
                      size="small"
                    >
                      ChkInArticle
                    </Button> */}

                  <StatusBadge
                    icon={<CheckCircleIcon fontSize="small" style={{ color: 'white' }} />}
                    label={bookingData?.BookStatus || 'Active'}
                    sx={{
                      color: 'white',
                      padding: 0,
                      '& .MuiChip-label': {
                        padding: 1,
                      },
                    }}
                  />
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <EmailIcon color="action" sx={{ mr: 1 }} />
                <Typography variant="body1" color="text.secondary">
                  {bookingData?.Email || 'N/A'}
                </Typography>
              </Box>

              <Divider sx={{ my: 1 }} />

              <Box sx={{ mb: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <IconButton
                    onClick={onPrev}
                    disabled={currentIndex === 0}
                    color="primary"
                  >
                    <ArrowBack />
                  </IconButton>
                  <Typography variant="body2">
                    Booking {currentIndex + 1} of {totalBookings}
                  </Typography>
                  <IconButton
                    onClick={onNext}
                    disabled={currentIndex === totalBookings - 1}
                    color="primary"
                  >
                    <ArrowForward />
                  </IconButton>
                </Stack>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={2.5}>
                  <StatCard>
                    <CardContent sx={{
                      display: 'flex', alignItems: 'center', '&:last-child': {
                        paddingBottom: 2,
                      },
                    }}>
                      <StatIcon bgcolor="#c2185b">
                        <PropertyIcon />
                      </StatIcon>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Hostel
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          {bookingData?.PropName || 'N/A'}
                        </Typography>
                      </Box>
                    </CardContent>
                  </StatCard>
                </Grid>
                <Grid item xs={12} sm={6} md={2.5}>
                  <StatCard>
                    <CardContent sx={{
                      display: 'flex', alignItems: 'center', '&:last-child': {
                        paddingBottom: 2,
                      },
                    }}>
                      <StatIcon bgcolor={theme.palette.warning.main}>
                        <ReceiptIcon />
                      </StatIcon>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Booking No
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          {bookingData?.BookNo || 'N/A'}
                        </Typography>
                        <Typography variant="h8">
                          {formatDate(bookingData?.BookDt)}
                        </Typography>
                      </Box>
                    </CardContent>
                  </StatCard>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <StatCard>
                    <CardContent sx={{
                      display: 'flex', alignItems: 'center', '&:last-child': {
                        paddingBottom: 2,
                      },
                    }}>
                      <StatIcon bgcolor={theme.palette.primary.main}>
                        <HomeIcon />
                      </StatIcon>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Room/Bed No
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          {bookingData?.RoomBedName || 'N/A'}
                        </Typography>
                      </Box>
                    </CardContent>
                  </StatCard>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <StatCard>
                    <CardContent sx={{
                      display: 'flex', alignItems: 'center', '&:last-child': {
                        paddingBottom: 2,
                      },
                    }}>
                      <StatIcon bgcolor="#4caf50">
                        <MoneyIcon />
                      </StatIcon>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Standard Rent
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          ₹{bookingData?.BedRent || '0'}
                        </Typography>
                      </Box>
                    </CardContent>
                  </StatCard>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <StatCard>
                    <CardContent sx={{
                      display: 'flex', alignItems: 'center',
                      '&:last-child': {
                        paddingBottom: 2,
                      },
                    }}>
                      <StatIcon bgcolor={theme.palette.secondary.main}>
                        <CalendarIcon />
                      </StatIcon>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Stay Duration
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          {formatDate(bookingData?.CheckinDt)} <br /> {formatDate(bookingData?.CheckoutDt)}
                        </Typography>
                      </Box>
                    </CardContent>
                  </StatCard>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </CardContent>
      </Card>
      {/* <ChkInArticleModal open={openModal} onClose={() => setOpenModal(false)}
        bookingData={bookingData}
      /> */}
    </Box>

  );
};

export default ProfileTab;